import React from 'react';
import type { ClearableSelectItem } from '../clearable-select';
import { ClearableSelect } from '../clearable-select';

/**
 * Enum representing the available score levels for filtering products
 */
enum ScoreLevel {
  All = 'all',
  VeryHigh = 'very_high',
  High = 'high',
  Moderate = 'moderate',
  Low = 'low',
  VeryLow = 'very_low',
}

const SCORE_LEVEL_ITEMS: ClearableSelectItem[] = [
  { id: ScoreLevel.VeryHigh, label: 'Very good', range: '80-100', color: 'bg-success-400' },
  { id: ScoreLevel.High, label: 'Good', range: '60-79', color: 'bg-success-300' },
  { id: ScoreLevel.Moderate, label: 'Moderate', range: '40-59', color: 'bg-yellow-400' },
  { id: ScoreLevel.Low, label: 'Low', range: '20-39', color: 'bg-warning-400' },
  { id: ScoreLevel.VeryLow, label: 'Very low', range: '0-19', color: 'bg-error-400' },
];

interface ScoreLevelSelectProps {
  /**
   * The selected score level
   * @optional
   * @type {ScoreLevel}
   * @default ScoreLevel.All
   */
  scoreLevel?: ScoreLevel;
  /**
   * The function to call when the score level changes
   * @required
   * @type {(scoreLevel: ScoreLevel) => void}
   */
  onScoreLevelChange: (scoreLevel: ScoreLevel) => void;
}

/**
 * Select component for choosing a score level filter.
 * Allows users to filter items by their listing score level (Very low, Low, Moderate, High, Very high, or All).
 *
 * @param props - The component props
 * @param props.scoreLevel - The currently selected score level
 * @param props.onScoreLevelChange - Callback fired when the score level changes
 */
function ScoreLevelSelect(props: ScoreLevelSelectProps) {
  const { scoreLevel = ScoreLevel.All, onScoreLevelChange } = props;

  const selectedKey = React.useMemo(() => (scoreLevel !== ScoreLevel.All ? scoreLevel : null), [scoreLevel]);

  const handleSelectionChange = (key: string | null) => {
    onScoreLevelChange((key ?? ScoreLevel.All) as ScoreLevel);
  };

  return (
    <ClearableSelect
      ariaLabel="Score level"
      placeholder="All scores"
      items={SCORE_LEVEL_ITEMS}
      selectedKey={selectedKey}
      onSelectionChange={handleSelectionChange}
    />
  );
}

export { ScoreLevelSelect, ScoreLevel };
export type { ScoreLevelSelectProps };
